import { supabase } from './supabase';
import { Attendance } from '../types';
import { getAttendanceStatus, calculateHoursWorked } from '../utils/philippinePayrollUtils';
import { getPhilippineDateTime, getPhilippineDate } from '../utils/dateUtils';

export const attendanceService = {
  // Get all attendance records
  async getAll(): Promise<Attendance[]> {
    const { data, error } = await supabase
      .from('attendance')
      .select('*')
      .order('date', { ascending: false });

    if (error) {
      console.error('Error fetching attendance records:', error);
      if (error.code === '42P01') {
        throw new Error('Database tables not found. Please run the setup script first.');
      }
      return [];
    }

    return data as Attendance[];
  },

  // Get attendance by ID
  async getById(id: string): Promise<Attendance | null> {
    const { data, error } = await supabase
      .from('attendance')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error(`Error fetching attendance with ID ${id}:`, error);
      return null;
    }

    return data as Attendance;
  },

  // Get attendance records by employee ID
  async getByEmployeeId(employeeId: string): Promise<Attendance[]> {
    const { data, error } = await supabase
      .from('attendance')
      .select('*')
      .eq('employee_id', employeeId)
      .order('date', { ascending: false });

    if (error) {
      console.error(`Error fetching attendance for employee ${employeeId}:`, error);
      return [];
    }

    return data as Attendance[];
  },

  // Get attendance records by date range
  async getByDateRange(startDate: string, endDate: string): Promise<Attendance[]> {
    const { data, error } = await supabase
      .from('attendance')
      .select('*')
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: false });

    if (error) {
      console.error(`Error fetching attendance for date range ${startDate} to ${endDate}:`, error);
      return [];
    }

    return data as Attendance[];
  },

  // Get attendance records by employee ID and date range (more efficient for payroll)
  async getByEmployeeAndDateRange(employeeId: string, startDate: string, endDate: string): Promise<Attendance[]> {
    const { data, error } = await supabase
      .from('attendance')
      .select('*')
      .eq('employee_id', employeeId)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: false });

    if (error) {
      console.error(`Error fetching attendance for employee ${employeeId} from ${startDate} to ${endDate}:`, error);
      return [];
    }

    return data as Attendance[];
  },

  // Create a new attendance record
  async create(attendance: Omit<Attendance, 'id' | 'created_at' | 'updated_at'>): Promise<Attendance | null> {
    const { data, error } = await supabase
      .from('attendance')
      .insert([{ ...attendance }])
      .select()
      .single();

    if (error) {
      console.error('Error creating attendance record:', error);
      return null;
    }

    return data as Attendance;
  },

  // Update an attendance record
  async update(id: string, attendance: Partial<Attendance>): Promise<Attendance | null> {
    // Get the current record to merge with updates
    const { data: currentRecord } = await supabase
      .from('attendance')
      .select('*')
      .eq('id', id)
      .single();

    if (!currentRecord) {
      console.error(`Attendance record with ID ${id} not found`);
      return null;
    }

    // Merge current record with updates
    const updatedRecord = { ...currentRecord, ...attendance };

    // Recalculate hours and status if time fields are being updated
    if (attendance.clock_in !== undefined || attendance.clock_out !== undefined || 
        attendance.break_start !== undefined || attendance.break_end !== undefined) {
      
      if (updatedRecord.clock_in && updatedRecord.clock_out) {
        // Calculate hours worked
        const hoursCalculation = calculateHoursWorked(
          updatedRecord.clock_in,
          updatedRecord.clock_out,
          updatedRecord.break_start || undefined,
          updatedRecord.break_end || undefined
        );
        
        // Update calculated fields
        updatedRecord.total_hours_worked = hoursCalculation.totalHours;
        updatedRecord.regular_hours = hoursCalculation.regularHours;
        updatedRecord.overtime_hours = hoursCalculation.overtimeHours;
        
        // Update status
        updatedRecord.status = getAttendanceStatus(
          updatedRecord.clock_in,
          updatedRecord.clock_out,
          updatedRecord.date
        );
      } else if (updatedRecord.clock_in && !updatedRecord.clock_out) {
        // If only clock_in is set, status should be incomplete
        updatedRecord.status = 'incomplete';
        updatedRecord.total_hours_worked = 0;
        updatedRecord.regular_hours = 0;
        updatedRecord.overtime_hours = 0;
      }
    }

    const { data, error } = await supabase
      .from('attendance')
      .update({ ...updatedRecord, updated_at: getPhilippineDateTime() })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating attendance with ID ${id}:`, error);
      return null;
    }

    return data as Attendance;
  },

  // Delete an attendance record
  async delete(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('attendance')
      .delete()
      .eq('id', id);

    if (error) {
      console.error(`Error deleting attendance with ID ${id}:`, error);
      return false;
    }

    return true;
  },

  // Clock in an employee
  async clockIn(employeeId: string, notes?: string): Promise<Attendance | null> {
    try {
      const today = getPhilippineDate();
      const now = getPhilippineDateTime();

      // Check if there's already an attendance record for today
      const { data: existingRecord } = await supabase
        .from('attendance')
        .select('*')
        .eq('employee_id', employeeId)
        .eq('date', today)
        .single();

      if (existingRecord) {
        // If already clocked in, don't allow another clock in
        if (existingRecord.clock_in && !existingRecord.clock_out) {
          console.warn('Employee already clocked in today');
          return existingRecord;
        }
        
        // Update existing record with clock in time
        const { data, error } = await supabase
          .from('attendance')
          .update({ 
            clock_in: now,
            clock_out: null,
            status: 'present'
          })
          .eq('id', existingRecord.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Create new attendance record
        const attendanceData: Omit<Attendance, 'id' | 'created_at' | 'updated_at'> = {
          employee_id: employeeId,
          date: today,
          clock_in: now,
          clock_out: null,
          break_start: null,
          break_end: null,
          total_hours_worked: 0,
          regular_hours: 0,
          overtime_hours: 0,
          status: 'present',
          notes: notes || undefined
        };

        return this.create(attendanceData);
      }
    } catch (error) {
      console.error('Error clocking in:', error);
      return null;
    }
  },

  // Clock out an employee
  async clockOut(employeeId: string): Promise<Attendance | null> {
    try {
      const today = getPhilippineDate();
      const now = getPhilippineDateTime();

      // Find today's attendance record
      const { data: existingRecord } = await supabase
        .from('attendance')
        .select('*')
        .eq('employee_id', employeeId)
        .eq('date', today)
        .single();

      if (!existingRecord) {
        console.error('No attendance record found for today');
        return null;
      }

      if (!existingRecord.clock_in) {
        console.error('Employee has not clocked in yet');
        return null;
      }

      if (existingRecord.clock_out) {
        console.warn('Employee already clocked out today');
        return existingRecord;
      }

      // Calculate hours worked using Philippine standards
      const hoursCalculation = calculateHoursWorked(
        existingRecord.clock_in,
        now,
        existingRecord.break_start || undefined,
        existingRecord.break_end || undefined
      );
      
      // Get attendance status
      const status = getAttendanceStatus(existingRecord.clock_in, now, today);

      // Update with clock out time, calculated hours, and status
      const { data, error } = await supabase
        .from('attendance')
        .update({ 
          clock_out: now,
          total_hours_worked: hoursCalculation.totalHours,
          regular_hours: hoursCalculation.regularHours,
          overtime_hours: hoursCalculation.overtimeHours,
          status: status
        })
        .eq('id', existingRecord.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error clocking out:', error);
      return null;
    }
  },

  // Start break
  async startBreak(employeeId: string): Promise<Attendance | null> {
    try {
      const today = getPhilippineDate();
      const now = getPhilippineDateTime();

      const { data: existingRecord } = await supabase
        .from('attendance')
        .select('*')
        .eq('employee_id', employeeId)
        .eq('date', today)
        .single();

      if (!existingRecord || !existingRecord.clock_in) {
        console.error('Employee must clock in first');
        return null;
      }

      const { data, error } = await supabase
        .from('attendance')
        .update({ break_start: now })
        .eq('id', existingRecord.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error starting break:', error);
      return null;
    }
  },

  // End break
  async endBreak(employeeId: string): Promise<Attendance | null> {
    try {
      const today = getPhilippineDate();
      const now = getPhilippineDateTime();

      const { data: existingRecord } = await supabase
        .from('attendance')
        .select('*')
        .eq('employee_id', employeeId)
        .eq('date', today)
        .single();

      if (!existingRecord || !existingRecord.break_start) {
        console.error('No active break found');
        return null;
      }

      const { data, error } = await supabase
        .from('attendance')
        .update({ break_end: now })
        .eq('id', existingRecord.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error ending break:', error);
      return null;
    }
  }
};